import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import MangaCard from '@/components/base/MangaCard';
import { getMangaByCategory, categoryMap } from '@/mocks/manga';

const validCategories = ['kr', 'jp', 'cn'];

export default function CategoryPage() {
  const { category = 'kr' } = useParams<{ category: string }>();
  const [activeCategory, setActiveCategory] = useState(
    validCategories.includes(category) ? category : 'kr'
  );

  const mangaList = getMangaByCategory(activeCategory);
  const categoryLabel = categoryMap[activeCategory] || '漫画';

  const handleCategoryChange = (cat: string) => {
    setActiveCategory(cat);
    window.history.pushState(null, '', `/category/${cat}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `香香漫画 - ${categoryLabel}分类`,
    description: `香香漫画网${categoryLabel}免费在线阅读，海量${categoryLabel}漫画全集，下拉式追更体验。`,
    url: `https://www.xiangxiangmh.com/category/${activeCategory}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '香香漫画',
      url: 'https://www.xiangxiangmh.com',
    },
  };

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: categoryLabel },
  ];

  const descriptions: Record<string, string> = {
    kr: '精选韩漫免费阅读，涵盖恋爱、奇幻、职场、复仇等多种题材。韩式唯美画风，精彩剧情，下拉式流畅阅读体验。',
    jp: '经典日漫在线免费阅读，从热血冒险到治愈日常，从纯爱校园到奇幻异世界，应有尽有。',
    cn: '优质国漫免费全集，玄幻修仙、都市搞笑、热血战斗，支持国产漫画，感受国漫崛起的力量。',
  };

  return (
    <>
      <SEO
        title={`香香漫画 - ${categoryLabel} | 免费${categoryLabel}在线阅读`}
        description={descriptions[activeCategory] || `香香漫画网${categoryLabel}免费在线阅读`}
        keywords={`香香漫画,${categoryLabel},免费阅读,漫画全集,下拉式,追更`}
        canonicalUrl={`https://www.xiangxiangmh.com/category/${activeCategory}`}
        ogType="website"
        schema={categorySchema}
      />
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb items={breadcrumbItems} />

        <section className="w-full max-w-[1400px] mx-auto px-4 md:px-6 pb-12">
          <div className="py-8">
            <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-2">
              {categoryLabel}
            </h1>
            <p className="text-sm text-foreground-500 max-w-2xl">
              {descriptions[activeCategory]}
            </p>
          </div>

          <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
            {validCategories.map((cat) => (
              <button
                key={cat}
                onClick={() => handleCategoryChange(cat)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeCategory === cat
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {categoryMap[cat]}
              </button>
            ))}
          </div>

          {mangaList.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
              {mangaList.map((manga) => (
                <MangaCard key={manga.id} manga={manga} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-book-open-line text-2xl text-foreground-300"></i>
              </div>
              <p className="text-foreground-500">暂无{categoryLabel}漫画</p>
            </div>
          )}

          {mangaList.length > 0 && (
            <div className="mt-8 text-center">
              <p className="text-sm text-foreground-400">
                共 {mangaList.length} 部{categoryLabel}漫画
              </p>
            </div>
          )}
        </section>
      </main>
      <Footer />
    </>
  );
}